// Enhanced filtering component for the ANR Page
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const FilterPanel = ({ 
  apiData, 
  kunden, 
  globalFilter, 
  setGlobalFilter, 
  onFilterChange, 
  onResetFilters 
}) => {
  // Basic filter state
  const [localFilter, setLocalFilter] = useState(globalFilter || '');
  
  // Advanced filters
  const [advancedFilters, setAdvancedFilters] = useState({
    anr: '',
    description: '',
    kundenId: null,
    status: '',
    minQuantity: '',
    maxQuantity: '',
  });
  
  // Separate ANR display value (what user sees in the input)
  const [anrDisplayValue, setAnrDisplayValue] = useState('');
  
  // Toggle state for advanced filter panel
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Years available in the data
  const [availableYears, setAvailableYears] = useState(['all', '23', '24', '25']);
  
  // Apply filters to the data
  const applyFilters = () => {
    // Pass all filters up to parent component for server-side processing
    // Format ANR for filtering only when applying filters
    const formattedANR = anrDisplayValue ? formatANRInput(anrDisplayValue) : '';
    
    onFilterChange({
      globalFilter: localFilter,
      advanced: {
        ...advancedFilters,
        anr: formattedANR
      }
    });
  };
  
  // Reset all filters
  const resetFilters = () => {
    setLocalFilter('');
    setAnrDisplayValue(''); // Reset ANR display value
    setAdvancedFilters({
      anr: '',
      description: '',
      kundenId: null,
      status: '',
      minQuantity: '',
      maxQuantity: '',
    });
    onResetFilters();
  };
  
  // Handle changes to the basic global filter
  const handleGlobalFilterChange = (e) => {
    setLocalFilter(e.target.value);
    setGlobalFilter(e.target.value);
  };
  
  // Update the advanced filter object when a filter changes
  const handleAdvancedFilterChange = (field, value) => {
    setAdvancedFilters({
      ...advancedFilters,
      [field]: value
    });
  };
  
  // Count active filters to display on the chip
  const countActiveFilters = () => {
    return Object.values(advancedFilters).filter(val => 
      val !== '' && val !== null && val !== undefined
    ).length;
  };
  
  // Format ANR input to ensure it's at least 4 digits with leading zeros
  const formatANRInput = (value) => {
    // Remove any non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // If it's empty, return empty string - this fixes the "0000" issue
    if (!digitsOnly) return '';
    
    // Only pad with leading zeros if there's actual input
    return digitsOnly.padStart(4, '0');
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      {/* Basic search filter */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            placeholder="Globale Suche..."
            value={localFilter}
            onChange={handleGlobalFilterChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              endAdornment: localFilter ? (
                <IconButton size="small" onClick={() => {
                  setLocalFilter('');
                  setGlobalFilter('');
                }}>
                  <ClearIcon />
                </IconButton>
              ) : null
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              endIcon={countActiveFilters() > 0 ? 
                <Chip 
                  size="small" 
                  label={countActiveFilters()} 
                  color="primary" 
                /> : null
              }
            >
              Erweiterte Filter
            </Button>
            
            <Button 
              variant="outlined"
              color="secondary"
              onClick={resetFilters}
            >
              Filter zurücksetzen
            </Button>
          </Box>
        </Grid>
      </Grid>
      
      {/* Advanced filters panel */}
      {showAdvancedFilters && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Erweiterte Filteroptionen
          </Typography>
          
          <Grid container spacing={2}>
            {/* ANR Filter with precise 4-digit search */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="ANR (genau)"
                value={anrDisplayValue}
                onChange={(e) => {
                  // Only allow digits in the input
                  const digitsOnly = e.target.value.replace(/\D/g, '');
                  setAnrDisplayValue(digitsOnly);
                  // Don't format the display value, only store digits as typed
                  handleAdvancedFilterChange('anr', digitsOnly);
                }}
                helperText="Mindestens 4 Ziffern, z.B. 123"
              />
            </Grid>
            
            {/* Description filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Auftragsbezeichnung"
                value={advancedFilters.description}
                onChange={(e) => handleAdvancedFilterChange('description', e.target.value)}
              />
            </Grid>
            
            {/* Kunden filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={kunden || []}
                getOptionLabel={(option) => 
                  option.konto && option.bezeichnung 
                    ? `${option.konto} - ${option.bezeichnung}`
                    : option.bezeichnung || option.konto || ''
                }
                value={advancedFilters.kundenId}
                onChange={(e, newValue) => handleAdvancedFilterChange('kundenId', newValue)}
                renderInput={(params) => <TextField {...params} label="Kunde" />}
                isOptionEqualToValue={(option, value) => 
                  option._id === (value?._id || '')
                }
              />
            </Grid>
            
            {/* Status filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={advancedFilters.status}
                  onChange={(e) => handleAdvancedFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">Alle</MenuItem>
                  <MenuItem value="ausstehend">Ausstehend</MenuItem>
                  <MenuItem value="fertig">Fertig</MenuItem>
                  <MenuItem value="anghalten">Angehalten</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Quantity Range filters */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Min. Menge"
                value={advancedFilters.minQuantity}
                onChange={(e) => handleAdvancedFilterChange('minQuantity', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Max. Menge"
                value={advancedFilters.maxQuantity}
                onChange={(e) => handleAdvancedFilterChange('maxQuantity', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={applyFilters}
            >
              Filter anwenden
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// Enhanced filter logic for implementation in the main component
const applyEnhancedFilters = (data, filters) => {
  if (!data || !filters) return data;
  
  let filteredData = [...data];
  
  // Apply global filter across multiple fields
  if (filters.globalFilter) {
    const searchTerm = filters.globalFilter.toLowerCase();
    filteredData = filteredData.filter(item => 
      // ANR search
      (item.ANR && item.ANR.toLowerCase().includes(searchTerm)) ||
      // Description search
      (item.Auftragsbezeichnung && item.Auftragsbezeichnung.toLowerCase().includes(searchTerm)) ||
      // Kunde search (both number and name)
      (item.kundenID && 
        ((item.kundenID.konto && item.kundenID.konto.toLowerCase().includes(searchTerm)) ||
         (item.kundenID.bezeichnung && item.kundenID.bezeichnung.toLowerCase().includes(searchTerm)))) ||
      // Other searchable fields
      (item.invoiceNo && item.invoiceNo.toLowerCase().includes(searchTerm)) ||
      (item.status && item.status.toLowerCase().includes(searchTerm))
    );
  }
  
  // Apply advanced filters if they exist
  if (filters.advanced) {
    const { anr, description, kundenId, status, minQuantity, maxQuantity } = filters.advanced;
    
    // Precise ANR filter - check if the ANR starts with the specified value
    if (anr) {
      filteredData = filteredData.filter(item => {
        // Extract the first part of ANR (before first dash)
        const anrParts = item.ANR.split('-');
        if (anrParts.length > 0) {
          const firstPart = anrParts[0];
          // Match exactly if the search string is the same length
          if (anr.length === firstPart.length) {
            return firstPart === anr;
          }
          // Otherwise check if it's a prefix
          return firstPart.startsWith(anr);
        }
        return false;
      });
    }
    
    // Description filter
    if (description) {
      const descSearch = description.toLowerCase();
      filteredData = filteredData.filter(item => 
        item.Auftragsbezeichnung && 
        item.Auftragsbezeichnung.toLowerCase().includes(descSearch)
      );
    }
    
    // Kunden filter
    if (kundenId) {
      filteredData = filteredData.filter(item => 
        item.kundenID && item.kundenID._id === kundenId._id
      );
    }
    
    // Status filter
    if (status) {
      filteredData = filteredData.filter(item => item.status === status);
    }
    
    // Quantity range filters
    if (minQuantity !== '') {
      const min = parseFloat(minQuantity);
      filteredData = filteredData.filter(item => 
        parseFloat(item.Menge || 0) >= min
      );
    }
    
    if (maxQuantity !== '') {
      const max = parseFloat(maxQuantity);
      filteredData = filteredData.filter(item => 
        parseFloat(item.Menge || 0) <= max
      );
    }
  }
  
  return filteredData;
};

// NOTE: The applyEnhancedFilters function has been moved to server-side implementation.
// Advanced filtering is now handled by the backend API in the getAllProjects endpoint.
// The following parameters are sent to the backend:
// - advancedAnr: Precise ANR filtering (first 4 digits)
// - description: Auftragsbezeichnung filter
// - kundenId: Customer ID filter
// - status: Project status filter
// - minQuantity: Minimum quantity filter
// - maxQuantity: Maximum quantity filter

export { FilterPanel };